/**
 * Email log — Step 19's second page. mailer.ts's contract: EVERY send
 * attempt writes a row here, whatever its fate — "did the customer get an
 * email?" must be answerable without grepping a server log. `staged` rows
 * are a park-for-review send (lib/mailer.ts's stageEmail) with no caller yet
 * in this build; this page is where a person actually sends or discards one.
 *
 * Draft-first on purpose: the only free text is the log's search box — a
 * filter, not a draft. Nothing here is authored.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Mail, X } from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { fullTime, relativeTime } from '../../lib/time';
import { useDebouncedUrlState, useUrlPatch, useUrlState } from '../../lib/urlState';
import { qk } from '../../lib/queryClient';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';

interface EmailLogEntry {
  id: string;
  ticketId: string | null;
  toAddress: string;
  subject: string;
  bodyHtml: string | null;
  status: 'staged' | 'sent' | 'failed' | 'discarded';
  error: string | null;
  createdAt: number;
  sentAt: number | null;
}

const STATUS_LABELS: Record<string, string> = {
  staged: 'Staged',
  sent: 'Sent',
  failed: 'Failed',
  discarded: 'Discarded',
};

// One saturated accent per row, per the house rule: amber = waiting
// (staged), emerald = success (sent), destructive = errors (failed),
// plain outline = discarded — a closed matter, no accent earned.
const STATUS_STYLES: Record<string, string> = {
  staged: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  sent: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  failed: 'border-destructive/40 bg-destructive/10 text-destructive',
  discarded: '',
};

function dayStart(v: string): number | null {
  if (!v) return null;
  const d = new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}
function dayEnd(v: string): number | null {
  const start = dayStart(v);
  return start === null ? null : start + 24 * 60 * 60 * 1000 - 1;
}

export function EmailLogPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useUrlState('status', '');
  const [search, setSearch] = useDebouncedUrlState('q');
  const [fromDate, setFromDate] = useUrlState('from', '');
  const [toDate, setToDate] = useUrlState('to', '');
  const [page, setPage] = useUrlState('page', '1');
  // Which message is open — the whole point of opening the email log is to
  // show somebody a specific delivery.
  const [expanded, setExpanded] = useUrlState('message', null);
  const patchUrl = useUrlPatch();

  const activeFilterCount = [!!status, !!search, !!fromDate, !!toDate].filter(Boolean).length;

  const params = new URLSearchParams({ limit: '50', page });
  if (status) params.set('status', status);
  if (search) params.set('q', search);
  const from = dayStart(fromDate);
  if (from !== null) params.set('from', String(from));
  const to = dayEnd(toDate);
  if (to !== null) params.set('to', String(to));

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.emailLog.list(params.toString()),
    queryFn: () =>
      apiFetch<{ entries: EmailLogEntry[]; total: number; page: number; pages: number }>(
        `/api/admin/email-log?${params}`,
      ),
    placeholderData: keepPreviousData,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: qk.emailLog.all });
  }
  const sendNow = useMutation({
    mutationFn: (id: string) => apiSend(`/api/admin/email-log/${id}/send`, 'POST'),
    onSuccess: invalidate,
  });
  const discard = useMutation({
    mutationFn: (id: string) => apiSend(`/api/admin/email-log/${id}/discard`, 'POST'),
    onSuccess: invalidate,
  });

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-center gap-3">
        <Mail className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Email log</h1>
        {data && <span className="text-xs text-muted-foreground">{data.total} entries</span>}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage('1');
          }}
          placeholder="Search by recipient or subject…"
          className="w-56"
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage('1');
          }}
          className="w-40"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage('1');
          }}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm dark:[color-scheme:dark]"
          aria-label="From date"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage('1');
          }}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm dark:[color-scheme:dark]"
          aria-label="To date"
        />
        {activeFilterCount > 0 && (
          <button
            onClick={() => patchUrl({ status: null, q: null, from: null, to: null, page: null })}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      {isError && (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Could not load the email log</p>
          <p className="text-xs text-destructive/80">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="text-xs text-destructive/80 underline hover:text-destructive"
          >
            Retry
          </button>
        </div>
      )}
      {isPending &&
        Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="mb-2 h-10 w-full" />)}

      <div className="divide-y rounded-lg border bg-card">
        {data?.entries.map((e) => {
          const isOpen = expanded === e.id;
          return (
            <div key={e.id}>
              <button
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-accent/50"
                onClick={() => setExpanded(isOpen ? null : e.id)}
              >
                {isOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <Badge variant="outline" className={cn('shrink-0', STATUS_STYLES[e.status])}>
                  {STATUS_LABELS[e.status]}
                </Badge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{e.subject}</span>
                  <span className="block truncate text-xs text-muted-foreground">To {e.toAddress}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground" title={fullTime(e.createdAt)}>
                  {relativeTime(e.createdAt)}
                </span>
              </button>
              {isOpen && (
                <div className="space-y-2 border-t bg-muted/30 px-9 py-3 text-xs">
                  {e.error && <p className="text-destructive">Error: {e.error}</p>}
                  {e.sentAt && <p className="text-muted-foreground">Sent {fullTime(e.sentAt)}</p>}
                  {e.bodyHtml && (
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto rounded border bg-background p-2"
                      dangerouslySetInnerHTML={{ __html: e.bodyHtml }}
                    />
                  )}
                  {e.status === 'staged' && (
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" disabled={sendNow.isPending} onClick={() => sendNow.mutate(e.id)}>
                        Send now
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={discard.isPending}
                        onClick={() => discard.mutate(e.id)}
                      >
                        Discard
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {data && data.entries.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">No emails match.</p>
        )}
      </div>
      {data && data.pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Page {data.page} of {data.pages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.page <= 1}
              onClick={() => setPage(String(data.page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.page >= data.pages}
              onClick={() => setPage(String(data.page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
